import React, { useState, useEffect, useRef } from 'react';
import { supabase } from './lib/supabase';
import './AdminChat.css';

function AdminChat() {
  const [conversations, setConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');
  const messagesEndRef = useRef(null);
  const channelRef = useRef(null);

  const scrollToBottom = () => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const fetchConversations = async () => {
    setLoading(true);
    setError('');
    try {
      const { data: conv, error: e } = await supabase
        .from('chat_conversations')
        .select('id, user_id, guest_id, created_at, updated_at')
        .order('updated_at', { ascending: false });

      if (e) throw e;

      const ids = (conv || []).map((c) => c.user_id).filter(Boolean);
      let users = [];
      if (ids.length > 0) {
        const { data: u } = await supabase.from('users').select('id, full_name, email').in('id', ids);
        users = u || [];
      }
      const userMap = Object.fromEntries(users.map((u) => [u.id, u]));

      setConversations((conv || []).map((c) => ({
        ...c,
        label: c.user_id ? (userMap[c.user_id]?.full_name || userMap[c.user_id]?.email || 'User') : 'Guest',
        sub: c.user_id ? (userMap[c.user_id]?.email || '') : (c.guest_id || '')
      })));
    } catch (err) {
      console.error('AdminChat fetchConversations:', err);
      setError('Failed to load conversations.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 15000);
    return () => clearInterval(interval);
  }, []);

  const fetchMessages = async (convId) => {
    const { data, error: e } = await supabase
      .from('chat_messages')
      .select('id, sender_type, message_text, created_at')
      .eq('conversation_id', convId)
      .order('created_at', { ascending: true });
    if (!e) setMessages(data || []);
  };

  const subscribe = (convId) => {
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }
    const ch = supabase
      .channel('admin-chat-' + convId)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'chat_messages', filter: 'conversation_id=eq.' + convId }, (payload) => {
        setMessages((m) => [...m, payload.new]);
      })
      .subscribe();
    channelRef.current = ch;
  };

  const selectConversation = (c) => {
    setSelected(c);
    setMessages([]);
  };

  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (selected?.id) {
      fetchMessages(selected.id);
      subscribe(selected.id);
    }
  }, [selected?.id]);

  const send = async () => {
    const text = (input || '').trim();
    if (!text || !selected || sending) return;
    setSending(true);
    setInput('');
    setError('');
    try {
      const { error: e } = await supabase.from('chat_messages').insert([
        { conversation_id: selected.id, sender_type: 'admin', message_text: text }
      ]);
      if (e) throw e;
    } catch (err) {
      console.error('AdminChat send:', err);
      setError('Failed to send.');
      setInput(text);
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  return (
    <div className="admin-chat">
      <div className="admin-chat-header">
        <h1 className="admin-chat-title">Chat with customers</h1>
      </div>
      {error && <div className="admin-chat-error">{error}</div>}
      <div className="admin-chat-layout">
        <aside className="admin-chat-list">
          {loading ? (
            <div className="admin-chat-loading">Loading...</div>
          ) : conversations.length === 0 ? (
            <div className="admin-chat-empty">No conversations yet.</div>
          ) : (
            <ul>
              {conversations.map((c) => (
                <li
                  key={c.id}
                  className={`admin-chat-item ${selected?.id === c.id ? 'active' : ''}`}
                  onClick={() => selectConversation(c)}
                >
                  <span className="admin-chat-item-label">{c.label}</span>
                  {c.sub && <span className="admin-chat-item-sub">{c.sub}</span>}
                </li>
              ))}
            </ul>
          )}
        </aside>
        <main className="admin-chat-main">
          {!selected ? (
            <div className="admin-chat-placeholder">Select a conversation</div>
          ) : (
            <>
              <div className="admin-chat-main-header">
                <span>{selected.label}</span>
                {selected.sub && <span className="admin-chat-main-sub">{selected.sub}</span>}
              </div>
              <div className="admin-chat-messages">
                {messages.map((m) => (
                  <div key={m.id} className={`admin-chat-msg ${m.sender_type === 'admin' ? 'admin-chat-msg-admin' : 'admin-chat-msg-user'}`}>
                    <span className="admin-chat-msg-sender">{m.sender_type === 'admin' ? 'You' : selected.label}</span>
                    <p className="admin-chat-msg-text">{m.message_text}</p>
                    <span className="admin-chat-msg-time">{new Date(m.created_at).toLocaleString()}</span>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>
              <div className="admin-chat-footer">
                <textarea
                  placeholder="Type your reply..."
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  rows={2}
                  disabled={sending}
                />
                <button className="admin-chat-send" onClick={send} disabled={sending || !input.trim()}>Send</button>
              </div>
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default AdminChat;
